/**
 * FuarAlaniBilgiKartlari.jsx — the extra data of a fairground (venue) on its detail page (backlog #51, 2026-10-20; backend #99).
 *
 * Cards over what `GET /fuar/alani/{id}` already returns (one read, no new request): the logo, künye and address, the map pin,
 * the photo gallery (cover first) and the organisation chart (a tree, `children` nested).
 *
 * READ-ONLY by default. With `canEdit` (the viewer holds page.fuar.yonetim; the backend checks it again on every write) each card
 * gets its controls, and the page's `onChanged` is called after every successful write: it reloads the venue, so the cards always
 * show what the backend now holds (the write answers differ in shape, see fuarAlaniEkBilgiService).
 *   - Künye and Konum: "Düzenle" opens FuarAlaniProfilModal (künye, address, pin, logo).
 *   - Fotoğraflar: upload, make cover, caption, remove (the same controls as the company gallery).
 *   - Organizasyon: add a person (top level or under someone), edit, remove (FuarAlaniOrgDugumModal).
 *
 * Nothing here is firma-specific: the map is FirmaKonumHarita (read-only unless the modal makes it editable), and a stored
 * file URL (relative, /public/files/...) is made absolute by firmaFileSrc, the same way the company pages do.
 */
import React, { useRef, useState } from "react";
import { Row, Col, Card, CardBody, Badge, Button, Alert, Spinner, Input, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Pencil, Plus, Star, Trash2, Upload } from "lucide-react";
import { useTranslation } from "react-i18next";
import FirmaKonumHarita from "../Firma/FirmaKonumHarita";
import FuarAlaniProfilModal from "./FuarAlaniProfilModal";
import FuarAlaniOrgDugumModal, { duzlestir } from "./FuarAlaniOrgDugumModal";
import { firmaFileSrc } from "../../services/firmaMediaService";
import {
  deleteFuarAlaniOrgNode, deleteFuarAlaniPhoto, gorselUzantisiGecerli, hataMetni,
  setFuarAlaniPhotoCover, updateFuarAlaniPhoto, uploadFuarAlaniPhoto,
} from "../../services/fuarAlaniEkBilgiService";

/** The backend's limit per venue (FuarAlaniPhotoConstant.MAX_PHOTOS_PER_FUAR_ALANI); the backend refuses the 21st with its own message. */
const FOTO_MAX_ADET = 20;

function BilgiKarti({ baslik, actions, children }) {
  return (
    <Card className="h-100">
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="text-uppercase text-muted fs-12 fw-semibold mb-0">{baslik}</h6>
          {actions}
        </div>
        {children}
      </CardBody>
    </Card>
  );
}

function DuzenleButonu({ onClick }) {
  const { t: tx } = useTranslation("yonetim");
  return (
    <Button color="light" size="sm" onClick={onClick}>
      <Pencil size={13} className="me-1" />{tx("common:duzenle")}
    </Button>
  );
}

/** Logo (when there is one), künye text and address. */
export function FuarAlaniKunyeCard({ fuarAlaniId, alani, canEdit = false, onChanged }) {
  const { t: tx } = useTranslation("yonetim");
  const [modalAcik, setModalAcik] = useState(false);
  const logo = alani?.fuarAlaniLogoUrl ? firmaFileSrc(alani.fuarAlaniLogoUrl) : null;
  const kunye = (alani?.fuarAlaniKunye || "").trim();
  const adres = (alani?.fuarAlaniAddress || "").trim();
  return (
    <>
      <BilgiKarti baslik={tx("fuarAlaniDetay.kunye")} actions={canEdit ? <DuzenleButonu onClick={() => setModalAcik(true)} /> : null}>
        {logo && (
          <img
            src={logo} alt={tx("fuarAlaniDetay.fuarAlaniLogosu")} className="mb-3 d-block"
            style={{ maxWidth: 220, maxHeight: 110, objectFit: "contain" }}
          />
        )}
        {kunye
          ? <p className="mb-3" style={{ whiteSpace: "pre-line" }}>{kunye}</p>
          : <p className="text-muted small mb-3">{tx("fuarAlaniDetay.henuzKunyeGirilmedi")}</p>}
        <div className="text-muted fs-12 fw-semibold text-uppercase mb-1">{tx("fuarAlaniDetay.adres")}</div>
        {adres
          ? <div style={{ whiteSpace: "pre-line" }}><i className="bx bx-map me-1 align-middle text-muted" />{adres}</div>
          : <div className="text-muted small">{tx("fuarAlaniDetay.henuzAdresGirilmedi")}</div>}
      </BilgiKarti>
      {modalAcik && (
        <FuarAlaniProfilModal fuarAlaniId={fuarAlaniId} alani={alani} onClose={() => setModalAcik(false)} onChanged={onChanged} />
      )}
    </>
  );
}

/** The map pin; read-only here, set in the profile modal. */
export function FuarAlaniKonumCard({ fuarAlaniId, alani, canEdit = false, onChanged }) {
  const { t: tx } = useTranslation("yonetim");
  const [modalAcik, setModalAcik] = useState(false);
  const lat = alani?.latitude;
  const lng = alani?.longitude;
  const pinVar = lat != null && lng != null;
  return (
    <>
      <BilgiKarti baslik={tx("fuarAlaniDetay.konum")} actions={canEdit ? <DuzenleButonu onClick={() => setModalAcik(true)} /> : null}>
        {pinVar ? (
          <>
            <div className="rounded-3 overflow-hidden border mb-2">
              <FirmaKonumHarita lat={Number(lat)} lng={Number(lng)} height={260} />
            </div>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 small">
              <span className="text-muted">{tx("fuarAlaniDetay.koordinat")}: {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}</span>
              <a
                href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=16/${lat}/${lng}`}
                target="_blank" rel="noopener noreferrer"
              >
                {tx("fuarAlaniDetay.haritadaAc")}
              </a>
            </div>
          </>
        ) : (
          <p className="text-muted small mb-0">{tx("fuarAlaniDetay.konumIsaretlenmedi")}</p>
        )}
      </BilgiKarti>
      {modalAcik && (
        <FuarAlaniProfilModal fuarAlaniId={fuarAlaniId} alani={alani} onClose={() => setModalAcik(false)} onChanged={onChanged} />
      )}
    </>
  );
}

const BOS_BASLIK_MODAL = { isOpen: false, foto: null, deger: "", kaydediliyor: false };
const BOS_SIL_MODAL = { isOpen: false, foto: null, siliniyor: false };

/** The gallery, cover first (the backend already sorts it); a click opens the photo large. */
export function FuarAlaniFotograflarCard({ fuarAlaniId, photos = [], canEdit = false, onChanged }) {
  const { t: tx } = useTranslation("yonetim");
  const dosyaRef = useRef(null);
  const [onizleme, setOnizleme] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [baslikModal, setBaslikModal] = useState(BOS_BASLIK_MODAL);
  const [silModal, setSilModal] = useState(BOS_SIL_MODAL);
  const [islemdeki, setIslemdeki] = useState(null);

  async function dosyaSecildi(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!gorselUzantisiGecerli(file)) { setHata(tx("fuarAlaniDetay.gorselTuruGecersiz")); return; }
    if (photos.length >= FOTO_MAX_ADET) { setHata(tx("fuarAlaniDetay.enFazlaFotograf", { n: FOTO_MAX_ADET })); return; }
    setYukleniyor(true);
    setHata(null);
    try {
      await uploadFuarAlaniPhoto(fuarAlaniId, file);
      await onChanged?.();
    } catch (err) {
      setHata(hataMetni(err, tx("fuarAlaniDetay.fotografYuklenemedi")));
    } finally {
      setYukleniyor(false);
    }
  }

  async function kapakYap(foto) {
    setIslemdeki(foto.id);
    setHata(null);
    try {
      await setFuarAlaniPhotoCover(fuarAlaniId, foto.id);
      await onChanged?.();
    } catch (err) {
      setHata(hataMetni(err, tx("fuarAlaniDetay.kapakGuncellenemedi")));
    } finally {
      setIslemdeki(null);
    }
  }

  async function baslikKaydet() {
    setBaslikModal((m) => ({ ...m, kaydediliyor: true }));
    setHata(null);
    try {
      await updateFuarAlaniPhoto(fuarAlaniId, baslikModal.foto.id, { baslik: baslikModal.deger.trim() || null });
      await onChanged?.();
    } catch (err) {
      setHata(hataMetni(err, tx("fuarAlaniDetay.baslikKaydedilemedi")));
    } finally {
      setBaslikModal(BOS_BASLIK_MODAL);
    }
  }

  async function sil() {
    setSilModal((m) => ({ ...m, siliniyor: true }));
    setHata(null);
    try {
      await deleteFuarAlaniPhoto(fuarAlaniId, silModal.foto.id);
      await onChanged?.();
    } catch (err) {
      setHata(hataMetni(err, tx("fuarAlaniDetay.fotografKaldirilamadi")));
    } finally {
      setSilModal(BOS_SIL_MODAL);
    }
  }

  return (
    <>
      <Card>
        <CardBody>
          {canEdit && (
            <div className="d-flex justify-content-end mb-3">
              <input ref={dosyaRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="d-none" onChange={dosyaSecildi} />
              <Button
                color="primary" outline size="sm" disabled={yukleniyor || photos.length >= FOTO_MAX_ADET}
                title={photos.length >= FOTO_MAX_ADET ? tx("fuarAlaniDetay.enFazlaFotograf", { n: FOTO_MAX_ADET }) : undefined}
                onClick={() => dosyaRef.current?.click()}
              >
                {yukleniyor ? <Spinner size="sm" /> : <><Upload size={14} className="me-1" />{tx("common:yukle")}</>}
              </Button>
            </div>
          )}
          {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}
          {photos.length === 0 ? (
            <p className="text-muted small mb-0">{tx("fuarAlaniDetay.henuzFotografYok")}</p>
          ) : (
            <Row className="g-3">
              {photos.map((f) => (
                <Col xs={6} md={4} xl={3} key={f.id}>
                  <div className="border rounded-3 overflow-hidden h-100 position-relative">
                    <div role="button" title={f.baslik || tx("fuarAlaniDetay.buyut")} style={{ cursor: "zoom-in" }} onClick={() => setOnizleme(f)}>
                      <img
                        src={firmaFileSrc(f.url)} alt={f.baslik || tx("fuarAlaniDetay.fuarAlaniFotografi")}
                        style={{ width: "100%", height: 150, objectFit: "cover", display: "block" }}
                      />
                    </div>
                    {f.kapak && <Badge color="danger" className="position-absolute" style={{ top: 6, left: 6 }}>{tx("fuarAlaniDetay.kapak")}</Badge>}
                    <div className="px-2 py-1 d-flex align-items-center justify-content-between gap-1">
                      <span className="small text-truncate" title={f.baslik || ""}>{f.baslik || "—"}</span>
                      {canEdit && (
                        <span className="d-flex align-items-center gap-2 flex-shrink-0">
                          {islemdeki === f.id ? <Spinner size="sm" /> : (
                            <>
                              {!f.kapak && (
                                <Star
                                  size={14} role="button" title={tx("fuarAlaniDetay.kapakYap")} className="text-muted"
                                  style={{ cursor: "pointer" }} onClick={() => kapakYap(f)}
                                />
                              )}
                              <Pencil
                                size={14} role="button" title={tx("fuarAlaniDetay.basligiDuzenle")} className="text-muted"
                                style={{ cursor: "pointer" }}
                                onClick={() => setBaslikModal({ isOpen: true, foto: f, deger: f.baslik || "", kaydediliyor: false })}
                              />
                              <Trash2
                                size={14} role="button" title={tx("fuarAlaniDetay.kaldir")} className="text-danger"
                                style={{ cursor: "pointer" }}
                                onClick={() => setSilModal({ isOpen: true, foto: f, siliniyor: false })}
                              />
                            </>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={!!onizleme} toggle={() => setOnizleme(null)} size="xl" centered>
        <ModalHeader toggle={() => setOnizleme(null)}>{onizleme?.baslik || tx("fuarAlaniDetay.fuarAlaniFotografi")}</ModalHeader>
        <ModalBody className="text-center">
          {onizleme && (
            <img
              src={firmaFileSrc(onizleme.url)} alt={onizleme.baslik || tx("fuarAlaniDetay.fuarAlaniFotografi")}
              style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain" }}
            />
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={baslikModal.isOpen} toggle={() => setBaslikModal(BOS_BASLIK_MODAL)}>
        <ModalHeader toggle={() => setBaslikModal(BOS_BASLIK_MODAL)}>{tx("fuarAlaniDetay.fotografBasligi")}</ModalHeader>
        <ModalBody>
          <Input
            bsSize="sm" value={baslikModal.deger} placeholder={tx("fuarAlaniDetay.baslikBosBirakilabilir")}
            onChange={(e) => setBaslikModal((m) => ({ ...m, deger: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); baslikKaydet(); } }}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setBaslikModal(BOS_BASLIK_MODAL)} disabled={baslikModal.kaydediliyor}>
            {tx("common:iptal")}
          </Button>
          <Button color="primary" onClick={baslikKaydet} disabled={baslikModal.kaydediliyor}>
            {baslikModal.kaydediliyor ? <Spinner size="sm" /> : tx("common:kaydet")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={silModal.isOpen} toggle={() => setSilModal(BOS_SIL_MODAL)}>
        <ModalHeader toggle={() => setSilModal(BOS_SIL_MODAL)}>{tx("fuarAlaniDetay.fotografiKaldir")}</ModalHeader>
        <ModalBody className="small">
          <strong>{silModal.foto?.baslik || tx("fuarAlaniDetay.buFotograf")}</strong>{" "}{tx("fuarAlaniDetay.galeridenKaldirilacak")}
          {silModal.foto?.kapak && ` ${tx("fuarAlaniDetay.kapakFotografiydiSiradaki")}`}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setSilModal(BOS_SIL_MODAL)} disabled={silModal.siliniyor}>
            {tx("common:iptal")}
          </Button>
          <Button color="danger" onClick={sil} disabled={silModal.siliniyor}>
            {silModal.siliniyor ? <Spinner size="sm" /> : tx("fuarAlaniDetay.kaldir")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

function OrgDugumu({ dugum, canEdit, onEkle, onDuzenle, onSil }) {
  const { t: tx } = useTranslation("yonetim");
  const cocuklar = Array.isArray(dugum.children) ? dugum.children : [];
  return (
    <li className="mb-2">
      <div className="d-inline-flex align-items-start gap-3 border rounded-3 px-3 py-2 bg-light-subtle">
        <div className="d-flex flex-column">
          <span className="fw-semibold">{dugum.adSoyad}</span>
          {dugum.unvan && <span className="text-muted small">{dugum.unvan}</span>}
          {(dugum.telefon || dugum.email) && (
            <span className="small mt-1">
              {dugum.telefon && <span className="me-3" title={tx("fuarAlaniDetay.telefon")}><i className="bx bx-phone me-1 align-middle text-muted" />{dugum.telefon}</span>}
              {dugum.email && (
                <a href={`mailto:${dugum.email}`} title={tx("fuarAlaniDetay.eposta")}><i className="bx bx-envelope me-1 align-middle text-muted" />{dugum.email}</a>
              )}
            </span>
          )}
        </div>
        {canEdit && (
          <span className="d-flex align-items-center gap-2 flex-shrink-0 pt-1">
            <Plus
              size={15} role="button" title={tx("fuarAlaniDetay.altinaKisiEkle")} className="text-muted"
              style={{ cursor: "pointer" }} onClick={() => onEkle(dugum)}
            />
            <Pencil
              size={14} role="button" title={tx("fuarAlaniDetay.kisiDuzenle")} className="text-muted"
              style={{ cursor: "pointer" }} onClick={() => onDuzenle(dugum)}
            />
            <Trash2
              size={14} role="button" title={tx("fuarAlaniDetay.kisiKaldir")} className="text-danger"
              style={{ cursor: "pointer" }} onClick={() => onSil(dugum)}
            />
          </span>
        )}
      </div>
      {cocuklar.length > 0 && (
        <ul className="list-unstyled ms-4 mt-2 ps-3 border-start">
          {cocuklar.map((c) => (
            <OrgDugumu key={c.id} dugum={c} canEdit={canEdit} onEkle={onEkle} onDuzenle={onDuzenle} onSil={onSil} />
          ))}
        </ul>
      )}
    </li>
  );
}

/** The organisation chart: `nodes` are the top-level nodes of the tree the backend returns. */
export function FuarAlaniOrgSemasiCard({ fuarAlaniId, nodes = [], canEdit = false, onChanged }) {
  const { t: tx } = useTranslation("yonetim");
  // null = closed; { dugum, parentId }: dugum null = add under parentId (null = top level), a node = edit it
  const [modal, setModal] = useState(null);
  const [silinecek, setSilinecek] = useState(null);
  const [siliniyor, setSiliniyor] = useState(false);
  const [hata, setHata] = useState(null);

  async function sil() {
    setSiliniyor(true);
    setHata(null);
    try {
      await deleteFuarAlaniOrgNode(fuarAlaniId, silinecek.id);
      await onChanged?.();
    } catch (err) {
      setHata(hataMetni(err, tx("fuarAlaniDetay.kisiKaldirilamadi")));
    } finally {
      setSiliniyor(false);
      setSilinecek(null);
    }
  }

  return (
    <>
      <Card>
        <CardBody>
          {canEdit && (
            <div className="d-flex justify-content-end mb-3">
              <Button color="primary" outline size="sm" onClick={() => setModal({ dugum: null, parentId: null })}>
                <Plus size={14} className="me-1" />{tx("fuarAlaniDetay.kisiEkle")}
              </Button>
            </div>
          )}
          {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}
          {nodes.length === 0 ? (
            <p className="text-muted small mb-0">{tx("fuarAlaniDetay.henuzOrgSemasiYok")}</p>
          ) : (
            <ul className="list-unstyled mb-0">
              {nodes.map((n) => (
                <OrgDugumu
                  key={n.id} dugum={n} canEdit={canEdit}
                  onEkle={(d) => setModal({ dugum: null, parentId: d.id })}
                  onDuzenle={(d) => setModal({ dugum: d, parentId: null })}
                  onSil={(d) => { setHata(null); setSilinecek(d); }}
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {modal && (
        <FuarAlaniOrgDugumModal
          fuarAlaniId={fuarAlaniId} dugum={modal.dugum} varsayilanParentId={modal.parentId} tumu={duzlestir(nodes)}
          onClose={() => setModal(null)} onChanged={onChanged}
        />
      )}

      <Modal isOpen={!!silinecek} toggle={() => setSilinecek(null)} centered>
        <ModalHeader toggle={() => setSilinecek(null)}>{tx("fuarAlaniDetay.kisiKaldir")}</ModalHeader>
        <ModalBody className="small">{tx("fuarAlaniDetay.kisiKaldirOnay", { ad: silinecek?.adSoyad })}</ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setSilinecek(null)} disabled={siliniyor}>{tx("common:iptal")}</Button>
          <Button color="danger" onClick={sil} disabled={siliniyor}>
            {siliniyor ? <Spinner size="sm" /> : tx("fuarAlaniDetay.kaldir")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
